import { useEffect, useState } from 'react'
import './ScrollJump.css'

/**
 * Jump to the top or the bottom of a long page.
 *
 * The register runs to 40 rows a page and the report to several hundred, and the
 * useful destinations are both ends — the controls and summary at the top, the
 * pager and the last rows at the bottom. Reaching either meant a long drag on a
 * trackpad or a phone.
 *
 * Deliberately not shown on a short page: a control that scrolls a page which
 * does not scroll is noise, and the check is cheap.
 */
export function ScrollJump() {
  const [state, setState] = useState({ show: false, atTop: true, atBottom: false })

  useEffect(() => {
    function measure() {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight

      // Under a viewport and a half there is nothing worth jumping over.
      if (scrollable < window.innerHeight * 0.5) {
        setState({ show: false, atTop: true, atBottom: false })
        return
      }

      const y = window.scrollY
      setState({
        show: true,
        atTop: y < 80,
        // A tolerance rather than an equality: sub-pixel layout and mobile
        // address-bar resizing mean the exact bottom is rarely hit precisely.
        atBottom: y > scrollable - 80,
      })
    }

    measure()

    // passive: this fires constantly and must never delay the scroll itself.
    window.addEventListener('scroll', measure, { passive: true })
    window.addEventListener('resize', measure)

    // The register swaps a page of rows without any scroll or resize happening,
    // which changes the document height under the buttons.
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)

    return () => {
      window.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
      observer.disconnect()
    }
  }, [])

  if (!state.show) return null

  function jump(to: 'top' | 'bottom') {
    window.scrollTo({
      top: to === 'top' ? 0 : document.documentElement.scrollHeight,
      // Smooth here, unlike the route change: this movement was asked for, and
      // seeing the page travel is what tells the user which way they went.
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    })
  }

  return (
    <div className="jump" role="group" aria-label="Scroll">
      <button
        type="button"
        className="jump__btn"
        onClick={() => jump('top')}
        disabled={state.atTop}
        title="Go to top"
        aria-label="Go to top"
      >
        ↑
      </button>
      <button
        type="button"
        className="jump__btn"
        onClick={() => jump('bottom')}
        disabled={state.atBottom}
        title="Go to bottom"
        aria-label="Go to bottom"
      >
        ↓
      </button>
    </div>
  )
}
